import TaskAssignee from "@/components/TaskAssignee";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, ArrowRight, AlertTriangle, ListChecks, LayoutList } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import api from "@/lib/api";
import { PageHeader, Card, Spinner, Empty } from "@/components/common";
import { StatusPill, PrioPill, VisPill, Progress, KundeAmZug, PRIORITIES, fmtDate, inputCls } from "./taskUtils";
import { useI18n } from "@/context/I18nContext";
import { taskNextStep } from "../../lib/taskWorkflow";

const VIS_FILTERS = [["alle", "Alle"], ["intern", "Intern"], ["kundensichtbar", "Kundensichtbar"], ["freigabe", "Freigabe"], ["kundenhinweis", "Kundenhinweis"]];

const EMPTY = {
  client_id: "", title: "", description: "", status: "offen", priority: "mittel",
  visibility: "intern", source: "Manuell", assignee: "KEWAG Team", assignee_id: null, next_action: "",
  due_date: "", customer_hint: "", internal_note: "", estimated_effort: "", checklist: [],
};

export default function Tasks({ approvalsOnly = false }) {
  const { t: tr } = useI18n();
  const nav = useNavigate();
  const [tasks, setTasks] = useState(null);
  const [error, setError] = useState("");
  const [approvalFilter, setApprovalFilter] = useState("pending");
  const [onlyMine, setOnlyMine] = useState(false);
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [clientsError, setClientsError] = useState("");
  const clientsRequest = useRef(0);
  const [cfg, setCfg] = useState(null);
  const [filter, setFilter] = useState("alle");
  const [visFilter, setVisFilter] = useState("alle");
  const [q, setQ] = useState("");
  const [params, setParams] = useSearchParams();
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [tpl, setTpl] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const savingRequest = useRef(false);
  const newTaskButton = useRef(null);
  const titleInput = useRef(null);

  const loadClients = async () => {
    const request = ++clientsRequest.current;
    setClientsLoading(true); setClientsError("");
    try {
      const { data } = await api.get("/clients");
      if (!Array.isArray(data)) throw new Error("Invalid client list");
      if (request === clientsRequest.current) setClients(data);
    } catch {
      if (request === clientsRequest.current) {
        setClients([]);
        setClientsError(tr("Kunden konnten nicht geladen werden. Bitte erneut versuchen."));
      }
    } finally {
      if (request === clientsRequest.current) setClientsLoading(false);
    }
  };

  const load = () => {
    setError("");
    return Promise.all([api.get("/tasks"), api.get("/tasks/config")])
      .then(([list, config]) => { setTasks(list.data); setCfg(config.data); })
      .catch(e => setError(e.response?.data?.detail || tr("Aufgaben konnten nicht geladen werden.")));
  };
  useEffect(() => {
    load();
    loadClients();
    return () => { clientsRequest.current++; };
  }, []);
  useEffect(() => {
    if (params.get("new") === "1") { setShowNew(true); params.delete("new"); setParams(params, { replace: true }); }
  }, [params, setParams]);

  const counts = useMemo(() => {
    const c = { gesamt: 0, offen: 0, in_arbeit: 0, warten_auf_kunde: 0, pruefung: 0, ueberfaellig: 0, heute: 0, woche: 0 };
    const weekAgo = new Date(Date.now() - 7 * 864e5).toISOString().slice(0, 10);
    (tasks || []).forEach((t) => {
      c.gesamt++;
      if (["offen", "in_arbeit", "warten_auf_kunde", "pruefung"].includes(t.status)) c[t.status]++;
      if (t.overdue) c.ueberfaellig++;
      if (t.due_today) c.heute++;
      if (t.status === "erledigt" && (t.completed_at || t.created_at || "") >= weekAgo) c.woche++;
    });
    return c;
  }, [tasks]);

  if (error) return <div role="alert" className="p-5 text-sm"><p>{error}</p><button className="mt-3 min-h-[44px] underline" onClick={load}>{tr("Erneut versuchen")}</button></div>;
  if (!tasks || !cfg) return <Spinner />;
  const slabel = (k) => tr(cfg.status_labels[k] || k);
  const vlabel = (k) => tr(cfg.visibility_labels[k] || k);

  const changeStatus = async (t, status) => { try { await api.put(`/tasks/${t.id}`, { status, approval_revision: t.approval_revision || 0 }); toast.success(tr("Status aktualisiert")); load(); } catch (e) { toast.error(e.response?.data?.detail || tr("Konnte nicht speichern.")); } };

  const applyTemplate = (id) => {
    setTpl(id);
    const tp = (cfg.templates || []).find((x) => x.id === id);
    if (!tp) return;
    setForm((f) => ({
      ...f, title: tp.title, description: tp.description, next_action: tp.next_action,
      source: tp.source, visibility: tp.visibility, priority: tp.priority,
      checklist: (tp.checklist || []).map((text) => ({ id: globalThis.crypto?.randomUUID?.() || String(Math.random()), text, done: false })),
    }));
  };

  // Validierung
  const errs = [];
  const warns = [];
  if (showNew) {
    if (!form.client_id) errs.push(tr("Kunde / Projekt fehlt."));
    if (!form.title.trim()) errs.push(tr("Titel fehlt."));
    if (!form.due_date) errs.push(tr("Fälligkeit fehlt."));
    if (!form.assignee) errs.push(tr("Zuständig fehlt."));
    if (!form.source) errs.push(tr("Quelle fehlt."));
    if (!form.next_action.trim()) errs.push(tr("Nächste Aktion fehlt."));
    if (["kundensichtbar", "freigabe", "kundenhinweis"].includes(form.visibility) && !form.customer_hint.trim())
      warns.push(tr("Aufgabe ist kundensichtbar, aber es fehlt ein Kundenhinweis."));
  }

  const save = async () => {
    if (savingRequest.current || clientsLoading || clientsError || !cfg.can_write || approvalsOnly) return;
    if (errs.length || !clients.some(c => c.id === form.client_id)) { toast.error(tr("Bitte Pflichtfelder prüfen.")); return; }
    savingRequest.current = true;
    setSaveError("");
    setSaving(true);
    try {
      const { data } = await api.post("/tasks", { ...form, due_date: form.due_date || null });
      toast.success(tr("Aufgabe erstellt"));
      setShowNew(false); setForm(EMPTY); setTpl(""); load();
      if (data.id) nav(`/admin/tasks/${data.id}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const message = typeof detail === "string" ? detail : tr("Konnte Aufgabe nicht erstellen.");
      setSaveError(message); toast.error(message);
    }
    finally { savingRequest.current = false; setSaving(false); }
  };

  const ql = q.trim().toLowerCase();
  const approvalTasks = tasks.filter(t => t.visibility === "freigabe" || t.needs_client_approval || t.status === "warten_auf_kunde");
  const approvalOpen = approvalTasks.filter(t => !t.approved && !["erledigt", "archiviert"].includes(t.status));
  const shown = (approvalsOnly ? approvalTasks : tasks).filter((t) => {
    if (approvalsOnly && approvalFilter === "pending" && (t.approved || ["erledigt", "archiviert"].includes(t.status))) return false;
    if (approvalsOnly && approvalFilter === "approved" && !t.approved) return false;
    if (onlyMine && (!cfg.current_user_id || t.assignee_id !== cfg.current_user_id)) return false;
    if (filter !== "alle" && t.status !== filter) return false;
    if (visFilter !== "alle" && t.visibility !== visFilter) return false;
    if (ql && !`${t.title} ${t.company_name} ${t.assignee} ${t.next_action} ${t.source}`.toLowerCase().includes(ql)) return false;
    return true;
  });

  const SUMMARY = [
    ["gesamt", "Aufgaben gesamt", false], ["offen", "Offen", false], ["in_arbeit", "In Arbeit", false],
    ["warten_auf_kunde", "Warten auf Kunde", false], ["pruefung", "Prüfung / Freigabe", false],
    ["ueberfaellig", "Überfällig", true], ["heute", "Heute fällig", false], ["woche", "Erledigt (Woche)", false],
  ];

  return (
    <div className="fade-in">
      <PageHeader title={tr(approvalsOnly ? "Freigaben" : "Aufgaben")} subtitle={tr(approvalsOnly ? "Sehen, worauf Kunden warten und was bereits freigegeben wurde." : "Wer macht was bis wann? Aufgaben zuweisen und den nächsten Schritt festhalten.")}>
        {!approvalsOnly && cfg.can_write && <button ref={newTaskButton} onClick={() => { setSaveError(""); setShowNew(true); }} data-testid="new-task-btn"
          className="bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2.5 px-5 rounded-lg transition-colors inline-flex items-center gap-2">
          <Plus className="w-4 h-4" /> {tr("Neue Aufgabe")}
        </button>}
      </PageHeader>

      {!cfg.can_write && <p className="mb-4 text-sm text-amber-200" data-testid="tasks-read-only">{tr("Nur Lesezugriff")}</p>}
      {approvalsOnly && <div className="flex flex-wrap gap-2 mb-5" aria-label={tr("Freigaben filtern")}>
        {[["pending", "Warten auf Kunde", approvalOpen.length], ["approved", "Freigegeben", approvalTasks.filter(t => t.approved).length], ["all", "Alle", approvalTasks.length]].map(([key, label, count]) => <button key={key} aria-pressed={approvalFilter === key} data-testid={`approval-filter-${key}`} onClick={() => setApprovalFilter(key)} className={`min-h-[44px] px-4 rounded-lg text-sm font-semibold ${approvalFilter === key ? "bg-[#A3E635] text-black" : "bg-white/5 text-gray-300"}`}>{tr(label)} · {count}</button>)}
      </div>}
      {!approvalsOnly && <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-6" data-testid="task-summary">
        {SUMMARY.map(([k, lbl, danger]) => (
          <Card key={k} className={`p-4 ${danger && counts[k] > 0 ? "border-red-500/40" : ""}`} data-testid={`task-stat-${k}`}>
            <div className={`text-2xl font-black tracking-tight ${danger && counts[k] > 0 ? "text-red-300" : ""}`}>{counts[k]}</div>
            <div className="text-[11px] text-gray-400 mt-0.5">{tr(lbl)}</div>
          </Card>
        ))}
      </div>}

      {!approvalsOnly && <div className="flex gap-2 mb-4 flex-wrap" data-testid="task-vis-filter">
        {VIS_FILTERS.map(([k, lbl]) => (
          <button key={k} onClick={() => setVisFilter(k)} data-testid={`task-visfilter-${k}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${visFilter === k ? "bg-white text-black" : "bg-white/5 text-gray-400 hover:text-white"}`}>{tr(lbl)}</button>
        ))}
      </div>}

      <div className="flex flex-wrap gap-2 mb-4">
        {[[false, "Alle Aufgaben"], [true, "Meine Aufgaben"]].map(([mine, label]) => <button key={label} data-testid={mine ? "tasks-only-mine" : "tasks-all-assignees"} aria-pressed={onlyMine === mine} onClick={() => setOnlyMine(mine)} className={`min-h-[44px] px-4 rounded-lg text-sm font-semibold ${onlyMine === mine ? "bg-white text-black" : "bg-white/5 text-gray-300"}`}>{tr(label)}</button>)}
      </div>
      <div className="relative flex-1 min-w-0 mb-4">
        <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="task-search"
          placeholder={tr("Suche nach Titel, Kunde, Zuständige, nächste Aktion...")} className={`${inputCls} pl-9`} />
      </div>

      <div className="flex gap-2 mb-5 flex-wrap items-center">
        <LayoutList className="w-4 h-4 text-gray-500" />
        {["alle", ...cfg.status_order].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`task-filter-${s}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${filter === s ? "bg-[#A3E635] text-black" : "bg-white/5 text-gray-400 hover:text-white"}`}>
            {s === "alle" ? tr("Alle") : slabel(s)}
          </button>
        ))}
      </div>

      {shown.length === 0 ? <Empty text={tr("Keine Aufgaben in dieser Ansicht.")} /> : (
        <>
          <Card className="p-0 hidden min-[1900px]:block overflow-x-auto">
            <table className="w-full text-sm min-w-[1100px]">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-gray-500 border-b border-white/10">
                  {[tr("Aufgabe"), tr("Kunde / Projekt"), tr("Status"), tr("Sichtbarkeit"), tr("Prio"), tr("Zuständig"), tr("Quelle"), tr("Nächste Aktion"), tr("Fortschritt"), tr("Fällig"), ""].map((h, hi) => (
                    <th key={hi} className="text-left font-bold px-3 py-3 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shown.map((t) => (
                  <tr key={t.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`task-row-${t.id}`}>
                    <td className="px-3 py-3 max-w-[220px]">
                      <div className="font-semibold truncate">{t.title}</div>
                      {t.kunde_am_zug && <KundeAmZug testid={`task-kundezug-${t.id}`} />}
                    </td>
                    <td className="px-3 py-3 text-gray-300 whitespace-nowrap">{t.company_name}</td>
                    <td className="px-3 py-3">
                      <select value={t.status} onChange={(e) => changeStatus(t, e.target.value)} disabled={!cfg.can_write} data-testid={`task-status-${t.id}`}
                        className="bg-black border border-white/10 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-[#A3E635]">
                        {cfg.status_order.map((s) => <option key={s} value={s} disabled={s === "erledigt" && t.visibility === "freigabe" && !t.approved}>{slabel(s)}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-3"><VisPill v={t.visibility} label={vlabel(t.visibility)} testid={`task-vis-${t.id}`} /></td>
                    <td className="px-3 py-3"><PrioPill p={t.priority} label={tr(t.priority_label || t.priority)} /></td>
                    <td className="px-3 py-3 text-xs text-gray-300 whitespace-nowrap">{t.assignee}</td>
                    <td className="px-3 py-3 text-xs text-gray-400 whitespace-nowrap">{t.source}</td>
                    <td className="px-3 py-3 text-xs text-gray-300 max-w-[160px] truncate" data-testid={`task-next-${t.id}`}>{t.next_action || "—"}</td>
                    <td className="px-3 py-3"><Progress done={t.progress?.done || 0} total={t.progress?.total || 0} /></td>
                    <td className={`px-3 py-3 text-xs whitespace-nowrap ${t.overdue ? "text-red-300 font-bold" : "text-gray-400"}`}>{fmtDate(t.due_date)}</td>
                    <td className="px-3 py-3">
                      <button onClick={() => nav(`/admin/tasks/${t.id}`)} data-testid={`task-open-${t.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#A3E635]/10 hover:bg-[#A3E635]/20 text-[#A3E635] text-xs font-bold whitespace-nowrap">
                        {tr("Öffnen")} <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>

          {/* Mobile / Tablet */}
          <div className="min-[1900px]:hidden grid md:grid-cols-2 gap-3">
            {shown.map((t) => (
              <Card key={t.id} className="p-4 min-w-0" data-testid={`task-card-${t.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm break-words">{t.title}</div>
                    <div className="text-xs text-gray-500 break-words">{t.company_name} · {t.source}</div>
                    {approvalsOnly && <div className="text-xs mt-1 text-amber-200">{tr(t.approved ? "Freigegeben" : t.visibility === "freigabe" ? "Freigabe ausstehend" : "Rückmeldung ausstehend")}</div>}
                  </div>
                  <PrioPill p={t.priority} label={tr(t.priority_label || t.priority)} />
                </div>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <StatusPill status={t.status} label={slabel(t.status)} />
                  <VisPill v={t.visibility} label={vlabel(t.visibility)} />
                  {t.kunde_am_zug && <KundeAmZug />}
                </div>
                <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                  <div><span className="text-gray-500">{tr("Zuständig")}:</span> <span className="text-gray-300">{t.assignee}</span></div>
                  <div className={t.overdue ? "text-red-300 font-bold" : "text-gray-400"}><span className="text-gray-500">{tr("Fällig")}:</span> {fmtDate(t.due_date)}</div>
                  <div className="col-span-2"><span className="text-gray-500">{tr("Nächste Aktion")}:</span> <span className="text-gray-300">{t.next_action || "—"}</span></div>
                </div>
                <p data-testid={`task-nextstep-${t.id}`} className="mt-3 text-xs font-semibold text-[#A3E635]">{tr("Nächster Schritt")}: {tr(taskNextStep(t).label)}</p>
                {t.progress?.total > 0 && <div className="mt-3"><Progress done={t.progress.done} total={t.progress.total} /></div>}
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <select value={t.status} onChange={(e) => changeStatus(t, e.target.value)} disabled={!cfg.can_write} data-testid={`task-card-status-${t.id}`}
                    className="bg-black border border-white/10 rounded-lg px-2 py-2 text-xs focus:outline-none focus:border-[#A3E635]">
                    {cfg.status_order.map((s) => <option key={s} value={s} disabled={s === "erledigt" && t.visibility === "freigabe" && !t.approved}>{slabel(s)}</option>)}
                  </select>
                  <button onClick={() => nav(`/admin/tasks/${t.id}`)} data-testid={`task-card-open-${t.id}`}
                    className="py-2 rounded-lg bg-[#A3E635] text-black text-xs font-bold inline-flex items-center justify-center gap-1.5">{tr("Öffnen")} <ArrowRight className="w-3.5 h-3.5" /></button>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}

      <Dialog open={showNew && cfg.can_write && !approvalsOnly} onOpenChange={open => { if (!savingRequest.current) setShowNew(open); }}>
        <DialogContent className="w-[calc(100vw_-_2rem)] max-w-[760px] max-h-[90dvh] overflow-y-auto min-w-0 bg-[#121217] border-white/10 text-white rounded-2xl p-4 sm:p-6" data-testid="new-task-modal"
          closeLabel={tr("Schliessen")} closeDisabled={saving}
          onOpenAutoFocus={e => { e.preventDefault(); titleInput.current?.focus(); }}
          onCloseAutoFocus={e => { e.preventDefault(); newTaskButton.current?.focus(); }}
          onEscapeKeyDown={e => { if (savingRequest.current) e.preventDefault(); }}
          onInteractOutside={e => e.preventDefault()}>
          <div className="pr-8">
            <DialogTitle className="flex items-center gap-2 text-lg font-bold"><ListChecks className="w-5 h-5 text-[#A3E635]" />{tr("Neue Aufgabe")}</DialogTitle>
            <DialogDescription className="text-sm text-gray-300 mt-2">{tr("Kunde, Verantwortung und nächsten Schritt festhalten. Zusätzliche Angaben können Sie bei Bedarf öffnen.")}</DialogDescription>
          </div>
          <form onSubmit={e => { e.preventDefault(); save(); }} aria-busy={saving}>
          <fieldset disabled={saving} className="min-w-0 space-y-3">
            <Lbl t={tr("Vorlage (optional)")}>
              <select value={tpl} onChange={e => applyTemplate(e.target.value)} data-testid="task-template" className={inputCls}>
                <option value="">{tr("— ohne Vorlage —")}</option>
                {(cfg.templates || []).map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
              </select>
            </Lbl>
            <div className="grid sm:grid-cols-2 gap-3">
              <Lbl t={tr("Titel *")} full><input ref={titleInput} required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="task-title" className={inputCls} placeholder={tr("z. B. Startseite-Texte aktualisieren")} /></Lbl>
              <Lbl t={tr("Kunde / Projekt *")}>
                <select required disabled={clientsLoading || !!clientsError} value={form.client_id} onChange={e => setForm({ ...form, client_id: e.target.value, assignee_id: null, assignee: "KEWAG Team" })} data-testid="task-client" className={inputCls}>
                  <option value="">{tr("— wählen —")}</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                </select>
              </Lbl>
              <Lbl t={tr("Zuständig *")}>
                <TaskAssignee clientId={form.client_id} value={form.assignee} assigneeId={form.assignee_id} generic={cfg.assignees} t={tr} testId="task-assignee" className={inputCls} onChange={assignment => setForm({ ...form, ...assignment })} />
              </Lbl>
              <Lbl t={tr("Nächste Aktion *")}><input required value={form.next_action} onChange={e => setForm({ ...form, next_action: e.target.value })} data-testid="task-nextaction" className={inputCls} placeholder={tr("z. B. Bilder prüfen")} /></Lbl>
              <Lbl t={tr("Fällig am *")}><input required type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} data-testid="task-due" className={inputCls} /></Lbl>
              <Lbl t={tr("Sichtbarkeit *")} full>
                <select value={form.visibility} onChange={e => setForm({ ...form, visibility: e.target.value })} data-testid="task-visibility" className={inputCls}>
                  {Object.entries(cfg.visibility_labels).map(([k, v]) => <option key={k} value={k}>{tr(v)}</option>)}
                </select>
              </Lbl>
              <Lbl t={tr("Beschreibung")} full><textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="task-desc" rows={2} className={inputCls} /></Lbl>
              <Lbl t={tr("Kundenhinweis (für Cockpit sichtbar)")} full><textarea value={form.customer_hint} onChange={e => setForm({ ...form, customer_hint: e.target.value })} data-testid="task-customerhint" rows={2} className={inputCls} placeholder={tr("Was soll der Kunde sehen / tun?")} /></Lbl>
            </div>
            {clientsLoading && <p role="status" className="text-sm text-gray-300">{tr("Kunden werden geladen…")}</p>}
            {clientsError && <div role="alert" data-testid="task-clients-error" className="text-sm text-amber-200">
              <p>{clientsError}</p><button type="button" onClick={loadClients} className="min-h-[44px] underline">{tr("Erneut versuchen")}</button>
            </div>}
            {!clientsLoading && !clientsError && clients.length === 0 && <p className="text-sm text-amber-200">{tr("Keine auswählbaren Kunden vorhanden.")}</p>}
            <p className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-gray-300" data-testid="task-visibility-hint">
              {tr(form.visibility === "intern" ? "Diese Aufgabe bleibt intern bei KEWAG." : "Für das Kunden-Cockpit vorgesehen. Interne Notizen bleiben intern.")}
            </p>
            <details data-testid="task-extra-settings" className="rounded-xl border border-white/10 px-3">
              <summary className="min-h-[44px] flex items-center cursor-pointer text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#A3E635]">{tr("Zusätzliche Angaben und interne Notiz")}</summary>
              <div className="grid sm:grid-cols-2 gap-3 pb-3">
                <Lbl t={tr("Quelle *")}><select value={form.source} onChange={e => setForm({ ...form, source: e.target.value })} data-testid="task-source" className={inputCls}>{cfg.sources.map(s => <option key={s} value={s}>{s}</option>)}</select></Lbl>
                <Lbl t={tr("Priorität")}><select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} data-testid="task-priority" className={inputCls}>{PRIORITIES.map(([k, v]) => <option key={k} value={k}>{tr(v)}</option>)}</select></Lbl>
                <Lbl t={tr("Status")}><select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} data-testid="task-form-status" className={inputCls}>{cfg.status_order.map(s => <option key={s} value={s}>{slabel(s)}</option>)}</select></Lbl>
                <Lbl t={tr("Geschätzter Aufwand")}><input value={form.estimated_effort} onChange={e => setForm({ ...form, estimated_effort: e.target.value })} className={inputCls} placeholder={tr("z. B. 2 Std.")} /></Lbl>
                <Lbl t={tr("Interne Notiz (nie für Kunde)")} full><textarea value={form.internal_note} onChange={e => setForm({ ...form, internal_note: e.target.value })} data-testid="task-internalnote" rows={2} className={inputCls} /></Lbl>
              </div>
            </details>
            {form.checklist.length > 0 && <p className="text-sm text-gray-300">{tr("Checkliste aus Vorlage:")} {form.checklist.map(c => c.text).join(" · ")}</p>}
            {warns.length > 0 && <div className="space-y-1.5" data-testid="task-form-soft-warnings">{warns.map((w, i) => <p key={i} className="text-sm text-amber-300 flex items-start gap-2"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />{w}</p>)}</div>}
            {errs.length > 0 && <p className="text-sm text-gray-300" data-testid="task-form-warnings">{errs.join(" ")}</p>}
            {saveError && <p role="alert" data-testid="task-save-error" className="text-sm text-red-300">{saveError} {tr("Ihre Eingaben bleiben erhalten.")}</p>}
            <div className="flex flex-wrap justify-end gap-3 pt-3">
              <button type="button" onClick={() => setShowNew(false)} className="min-h-[44px] px-4 py-2 rounded-lg text-sm text-gray-300 hover:text-white hover:bg-white/5">{tr("Abbrechen")}</button>
              <button type="submit" disabled={saving || clientsLoading || !!clientsError || errs.length > 0} data-testid="task-save"
                className="min-h-[44px] bg-[#A3E635] hover:bg-[#BEF264] disabled:opacity-40 disabled:cursor-not-allowed text-black font-bold py-2 px-5 rounded-lg transition-colors">
                {saving ? tr("Speichern…") : tr("Aufgabe erstellen")}
              </button>
            </div>
          </fieldset>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Lbl = ({ t, children, full }) => (
  <label className={full ? "sm:col-span-2" : ""}>
    <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400">{t}</span>
    <div className="mt-1">{children}</div>
  </label>
);

